import type { ChannelDetailItem, ChannelDetailItemContent } from "@/lib/types";
import { CalendarYearView, CalendarYearViewProps } from "@/components/calendar/CalendarYearView";
import { formatDate } from "date-fns";
import { useQuery } from "@tanstack/react-query";
import { getChannelDetailContent } from "@/sideEffects/api";
import { selectActiveFirm } from "@/store/appSelectors";
import { useSelector } from "react-redux";
import { SelectedMediaPreview } from "@/pages/shared/SelectedMediaPreview";
import { LoadingSmall } from "@/components/LoadingSmall";

interface YearViewProps {
  date: CalendarYearViewProps<ChannelDetailItem>["date"];
  events?: CalendarYearViewProps<ChannelDetailItem>["events"];
}

const getChannelDetailContents = async (channelDetailIds: string[], firmGuid: string) => {
  const data: ChannelDetailItemContent[] = [];
  const promises = channelDetailIds.map((channelDetailId) => getChannelDetailContent(channelDetailId, firmGuid));
  const result = await Promise.all(promises);
  result.forEach((item) => {
    data.push(...item.data.result);
  });
  return data;
};

export const ChannelYearView = ({ date, events = [] }: YearViewProps) => {
  const activeFirm = useSelector(selectActiveFirm);
  const { data: channelDetailContents, isLoading: isChannelDetailContentsLoading } = useQuery({
    queryFn: () =>
      getChannelDetailContents(
        events.map((event) => String(event.id)),
        String(activeFirm?.firmGuid)
      ),
    queryKey: ["getChannelDetailContents", activeFirm?.firmGuid, events.map((event) => String(event.id))],
  });

  if (isChannelDetailContentsLoading) {
    return <LoadingSmall />;
  }

  return (
    <CalendarYearView
      date={date}
      events={events}
      renderEventDetails={(_event) => {
        return (
          <>
            {channelDetailContents?.map((content) => (
              <SelectedMediaPreview contentIntId={content.contentintId} contentGroupIntId={content.contentGroupintId} />
            ))}
          </>
        );
      }}
    />
  );
};

ChannelYearView.title = (date: Date) => {
  return formatDate(date, "yyyy");
};
